import {useAtom} from 'jotai';
import {atomWithStorage} from 'jotai/utils';
import {useCallback, useMemo} from 'react';

import type {AppTileId} from './dashboardModel';

/** Query param the App Platform overlay's "Add to home" button links with. */
export const ADD_APP_SEARCH_PARAM = 'addApp';

/** Mirrors the App Platform route slug rule (see `helpers/app_platform.validate_app_slug`). */
export const isValidAppSlug = (slug: string): boolean =>
  /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(slug) && !slug.includes('--');

export const toAppTileId = (slug: string): AppTileId => `app:${slug}`;

/** Slug for an `app:<slug>` tile id; null for built-in tiles or malformed ids. */
export const appSlugFromTileId = (tileId: string): string | null => {
  if (!tileId.startsWith('app:')) {
    return null;
  }
  const slug = tileId.slice('app:'.length);
  return isValidAppSlug(slug) ? slug : null;
};

/** App Platform slugs each viewer pinned to their home, keyed by employee user_id. */
export type PinnedAppsStore = Record<string, string[]>;

export const pinnedAppsAtom = atomWithStorage<PinnedAppsStore>('dashboard:pinnedApps', {});

const sanitizeSlugs = (slugs: unknown): string[] =>
  Array.isArray(slugs)
    ? slugs.filter(
        (slug, index): slug is string =>
          typeof slug === 'string' && isValidAppSlug(slug) && slugs.indexOf(slug) === index
      )
    : [];

export const usePinnedApps = (userId: string | undefined) => {
  const [store, setStore] = useAtom(pinnedAppsAtom);

  const slugs = useMemo(() => (userId ? sanitizeSlugs(store[userId]) : []), [store, userId]);

  const update = useCallback(
    (updater: (prev: string[]) => string[]) => {
      if (!userId) {
        return;
      }
      setStore((prev) => ({...prev, [userId]: updater(sanitizeSlugs(prev[userId]))}));
    },
    [userId, setStore]
  );

  /** Newest pins go first so a just-added app lands at the top of the board. */
  const pinApp = useCallback(
    (slug: string) => {
      if (isValidAppSlug(slug)) {
        update((prev) => [slug, ...prev.filter((existing) => existing !== slug)]);
      }
    },
    [update]
  );

  const unpinApp = useCallback(
    (slug: string) => update((prev) => prev.filter((existing) => existing !== slug)),
    [update]
  );

  return {slugs, pinApp, unpinApp};
};
